"use strict";
/**
 * Security service — provides role/permission checking for the current session.
 *
 * Hydrates from sessionStorage on construction (synchronous), and fetches from
 * the profile API only when the cache is missing or stale (JWT iat changed).
 *
 * Usage:
 *   Syn.security.isGranted('ROLE_EQUIPMENT_MANAGER')   // synchronous, works immediately
 *   await Syn.security.awaitGranted('ROLE_...')         // waits for load if needed
 *
 * The profile endpoint returns `rolesReal` which contains all reachable roles
 * from the Symfony role hierarchy, so both top-level and leaf checks work.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.security = void 0;
var STORAGE_KEY = 'syn_security';
var Security = /** @class */ (function () {
    function Security() {
        this.profile = null;
        this.loadPromise = null;
        this.loaded = false;
        this.hydrated = false;
    }
    /**
     * Check if the current user has a given role.
     * Synchronous — works immediately if sessionStorage has cached data.
     * Returns false if profile is not yet available.
     */
    Security.prototype.isGranted = function (role) {
        this.hydrateOnce();
        if (!this.profile)
            return false;
        return this.profile.rolesReal.includes(role);
    };
    /**
     * Async version — ensures profile is loaded before checking.
     * Use in components that mount before the profile might be ready.
     */
    Security.prototype.awaitGranted = function (role) {
        return __awaiter(this, void 0, Promise, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.load()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, this.isGranted(role)];
                }
            });
        });
    };
    /**
     * Load the profile from the API if not already cached/valid.
     * Safe to call multiple times — deduplicates requests.
     */
    Security.prototype.load = function () {
        return __awaiter(this, void 0, Promise, function () {
            return __generator(this, function (_a) {
                this.hydrateOnce();
                if (this.loaded)
                    return [2 /*return*/];
                if (!this.loadPromise) {
                    this.loadPromise = this.fetchProfile();
                }
                return [2 /*return*/, this.loadPromise];
            });
        });
    };
    Security.prototype.getUserIri = function () {
        var _a, _b;
        return (_b = (_a = this.profile) === null || _a === void 0 ? void 0 : _a['@id']) !== null && _b !== void 0 ? _b : null;
    };
    Security.prototype.getUserId = function () {
        var _a, _b;
        return (_b = (_a = this.profile) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
    };
    Security.prototype.getProfile = function () {
        return this.profile;
    };
    Security.prototype.isLoaded = function () {
        this.hydrateOnce();
        return this.loaded;
    };
    // ============ Private ============
    /**
     * Attempt to restore from sessionStorage (runs once).
     */
    Security.prototype.hydrateOnce = function () {
        var _a;
        if (this.hydrated)
            return;
        this.hydrated = true;
        try {
            var raw = sessionStorage.getItem(STORAGE_KEY);
            if (!raw)
                return;
            var cached = JSON.parse(raw);
            if ((_a = cached.profile) === null || _a === void 0 ? void 0 : _a.rolesReal) {
                this.profile = cached.profile;
                this.loaded = true;
            }
        }
        catch (_b) {
            sessionStorage.removeItem(STORAGE_KEY);
        }
    };
    /**
     * Persist to sessionStorage.
     */
    Security.prototype.persistToStorage = function () {
        if (!this.profile)
            return;
        try {
            var cached = { profile: this.profile };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cached));
        }
        catch (_a) {
            // Storage full or unavailable — non-critical
        }
    };
    Security.prototype.fetchProfile = function () {
        return __awaiter(this, void 0, Promise, function () {
            var response, data, err_1;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 3, , 4]);
                        return [4 /*yield*/, fetch('/api/v3/contacts/profile', {
                                headers: { 'Accept': 'application/ld+json' },
                                credentials: 'same-origin',
                            })];
                    case 1:
                        response = _c.sent();
                        if (!response.ok) {
                            console.warn('[Security] Failed to load profile:', response.status);
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, response.json()];
                    case 2:
                        data = _c.sent();
                        // The profile endpoint is a GetCollection with a custom controller
                        // that returns a single entity — API Platform may wrap it in a collection
                        if (data['hydra:member']) {
                            this.profile = (_a = data['hydra:member'][0]) !== null && _a !== void 0 ? _a : null;
                        }
                        else if (data['member']) {
                            this.profile = (_b = data['member'][0]) !== null && _b !== void 0 ? _b : null;
                        }
                        else {
                            // Direct entity response
                            this.profile = data;
                        }
                        if (this.profile) {
                            this.loaded = true;
                            this.persistToStorage();
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        err_1 = _c.sent();
                        console.warn('[Security] Failed to load profile:', err_1);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    return Security;
}());
exports.security = new Security();
